import React, { useEffect, useState } from 'react';
import { Route } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import isEmpty from 'lodash/isEmpty';

import {
	XIcon,
	EmailSecondary,
	GoogleButtonIcon,
	MsButtonIcon,
	CheckMarkGreen,
	CommunicationCenterIcon,
	CommunicationCenterIconHover,
	ScheduleIcon,
	ScheduleIconHover,
	NotificationBell,
	NotificationBellHover,
	NewFeatureIcon,
	NewFeatureIconWhite,
	NotificationsEmailIcon,
	NotificationsEmailIconWhite,
	NotificationsOpensIcon,
	NotificationsOpensIconWhite,
	CheckmarkIcon,
	LogoShort
} from '@/assets/images';

import ReportsIcon from '@/assets/images/reports.png';
import ReportsIconHovering from '@/assets/images/reports-hovering.png';
import KnowledgeBaseIcon from '@/assets/images/knowledge-normal.png';
import KnowledgeBaseIconHovering from '@/assets/images/knowledge-hovering.png';
import ScheduledEmail from '@/assets/images/scheduled-email.png';
import ScheduledEmailHovering from '@/assets/images/scheduled-email-hover.png';

import Link from '@/components/Link/Link';
import Popup, { PopupAlign, PopupTrigger } from '@/components/Popup/Popup';
import type { List, Location } from '@/modules/dashboard/dashboardTypes';
import { reportAddRequest } from '@/modules/report/reportActions';
import {
	logoutUserRequest,
	userToggleAddUserModal,
	userToggleAddBccModal,
	userCreateScheduleRequest,
	userGetNotifications,
	userPostNotificationsRead
} from '@/modules/user/userActions';
import type { User } from '@/modules/user/userTypes';
import { getUserEmail } from '@/services/authentication';
import {
	sendAnalytics,
	sendPublicAnalytics
} from '@/services/analyticsService';
import reportTimer from '@/utils/reportTimer';

import Logo from '../Logo/Logo';

import './Header.scss';
import NavBar from './NavBar/NavBar';

type Props = {
	locations: Location[];

	locationChange: (location: string) => void;

	onPasswordReset: () => void;

	onAddUser: () => void;

	lists: List[];

	listChange: (list: string) => void;

	isOnboarded: boolean;

	user: User;

	setNylasSyncStatus: (x: boolean) => void;

	setShow2faModal: (x: boolean) => void;
};

const Header = ({
	locations,
	locationChange,
	lists,
	listChange,
	onPasswordReset,
	isOnboarded,
	user,
	setNylasSyncStatus,
	setShow2faModal
}: Props): JSX.Element => {
	const [
		scheduleCreated,
		calAuthRequired,
		editableUser,
		newMessages,
		userNotifications,
		selectedList,
		selectedLocation,
		fbFreemiumSuccess,
		locationsIsLoading,
		listsIsLoading,
		authorizeIsLoading
	] = useSelector(
		(state: RootState) => [
			state.user.getIn(['authorize', 'user', 'scheduleLinkCreated']),
			state.user.getIn(['authorize', 'user', 'calendarAuth']),
			state.user.getIn(['editableUser', 'data']),
			state.user.getIn(['authorize', 'newMessages']),
			state.user.getIn(['authorize', 'user', 'notifications']),
			state.dashboard.getIn(['lists', 'selected']).toJS(),
			state.dashboard.getIn(['locations', 'selected']).toJS(),
			state.user.getIn(['facebookFreemiumSuccess'], false),
			state.dashboard.getIn(['locations', 'isLoading']),
			state.dashboard.getIn(['lists', 'isLoading']),
			state.user.getIn(['authorize', 'isLoading'])
		],
		shallowEqual
	);

	const [addLocationOpen, setAddLocationOpen] = useState(false);

	const [createScheduleOpen, setCreateScheduleOpen] = useState(false);
	const [createScheduleStarted, setCreateScheduleStarted] = useState(false);
	const [successModal, setSuccessModal] = useState(true);

	useEffect(() => {
		if (scheduleCreated && user && user.scheduleLink) {
			setCreateScheduleStarted(false);
			openScheduler();
		}
	}, [scheduleCreated]);

	useEffect(() => {
		if (calAuthRequired === true) {
			openCreateSchedule();
		}
	}, [calAuthRequired]);
	(window as any).hsConversationsSettings = {
		identificationEmail: getUserEmail()
	};

	const { pathname, search } = useLocation();

	useEffect(() => {
		const onCalAuthPage = window.location.pathname === '/calendarauth';
		if (onCalAuthPage && user.name) {
			setTimeout(() => {
				openCreateMeeting();
			}, 1000);
		}
	}, [user.name]);

	const dispatch = useDispatch();
	const reportBusinesses = () => dispatch(reportAddRequest());
	const getNotifications = () => dispatch(userGetNotifications());
	const postNotificationsRead = () => dispatch(userPostNotificationsRead());

	const [selectedListFilter, setSelectedListFilter] = useState<string>('');
	const [selectedLocationFilter, setSelectedLocationFilter] =
		useState<string>('');

	useEffect(() => {
		if (!isEmpty(selectedLocation)) {
			setSelectedLocationFilter(selectedLocation.id);
			return;
		}

		setSelectedLocationFilter('');
	}, [selectedLocation?.name]);

	useEffect(() => {
		if (!isEmpty(selectedList)) {
			setSelectedListFilter(selectedList.id);
			return;
		}

		setSelectedListFilter('');
	}, [selectedList?.id]);

	useEffect(() => {
		if (pathname === '/communication-center') {
			if (selectedListFilter) {
				setSelectedListFilter('');
			}

			if (selectedLocationFilter) {
				setSelectedLocationFilter('');
			}
		}
	}, [pathname]);

	const closeLocationDropdown = () => {
		const el = document.getElementById('header-input-location-element');
		if (el) {
			el.classList.remove('input-open');
		}
	};

	useEffect(() => {
		if (!locationsIsLoading && !listsIsLoading && !authorizeIsLoading) {
			const newlyAddedLocationId =
				localStorage.getItem('added_location_id');

			if (newlyAddedLocationId) {
				closeLocationDropdown();
				setSelectedLocationFilter(newlyAddedLocationId);
				locationChange(newlyAddedLocationId);
				setSelectedListFilter('');
			}
		}
	}, [locationsIsLoading, listsIsLoading, authorizeIsLoading]);

	useEffect(() => {
		const newlyAddedLocationId = localStorage.getItem('added_location_id');
		const userToken = localStorage.getItem('user_token');

		if (newlyAddedLocationId && !isEmpty(selectedLocation) && userToken) {
			localStorage.removeItem('added_location_id');
		}
	}, [selectedLocation]);

	useEffect(() => {
		if (user && user.email && !editableUser && !user?.facebookFreemium) {
			setTimeout(() => {
				getNotifications();
			}, 1000);
		}
	}, [user.email]);

	useEffect(() => {
		if (fbFreemiumSuccess) {
			sendPublicAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'FB Ads - Verified Email'
			});
		}
	}, [fbFreemiumSuccess]);

	const createSchedule = () => dispatch(userCreateScheduleRequest());

	const toggleAddUserModal = (payload: boolean) => {
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `Opened Add User Modal`
			});
		}
		dispatch(userToggleAddUserModal(payload));
	};

	const toggleAddBccModal = (payload: boolean) => {
		dispatch(userToggleAddBccModal(payload));
	};

	const logoutUser = () => {
		clearInterval(reportTimer());
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User logged out'
			});
		}

		if (process.env.REACT_SHOULD_REPORT) {
			reportBusinesses();
		}
		dispatch(logoutUserRequest());
	};

	// const handleChatOpen = () => {
	//   if (user && user.email) {
	//     sendAnalytics({
	//       type: 'track',
	//       userId: user.email,
	//       eventName: 'Opened chat window',
	//     })
	//   }
	//   const hsChat = (window as any).HubSpotConversations.widget

	//   const r = document.querySelector<HTMLElement>(':root')
	//   if (r) {
	//     r.style.setProperty('--hs-visible', 'initial')

	//     if (hsChat) {
	//       hsChat.open()
	//     }
	//   }
	// }

	const openCreateMeeting = () => {
		if (user) {
			if (user?.scheduleLink) {
				openScheduler();
				return;
			}
			setCreateScheduleStarted(true);
			createSchedule();
		}
	};

	const [copiedLink, setCopiedLink] = useState('');
	const [copiedTimeout, setCopiedTimeout] = useState<any>();

	const copyScheduleLink = (e, type) => {
		if (copiedTimeout && copiedLink === user.scheduleLink[type]) {
			return;
		}
		clearTimeout(copiedTimeout);
		setCopiedTimeout(null);
		const allBoxes = document.getElementsByClassName(
			'scheduler-modal-link-box'
		);
		for (let i = 0; i < allBoxes.length; i++) {
			allBoxes[i].classList.remove('copied');
		}
		if (copiedLink === user.scheduleLink[type]) {
			setCopiedLink('');
			copyLink('');
		} else {
			setCopiedLink(user.scheduleLink[type]);
			e.currentTarget.classList.add('copied');
			copyLink(user.scheduleLink[type]);
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User copied a scheduler link',
				properties: {
					meetingLength: type
				}
			});
			setCopiedTimeout(
				setTimeout(() => {
					removeCopiedClass();
					clearTimeout(copiedTimeout);
					setCopiedTimeout(null);
				}, 5000)
			);
		}
	};

	const removeCopiedClass = () => {
		const allBoxes = document.getElementsByClassName(
			'scheduler-modal-link-box'
		);
		for (let i = 0; i < allBoxes.length; i++) {
			allBoxes[i].classList.remove('copied');
		}
	};

	const copyLink = (link: any) => {
		const dummy = document.createElement('input');
		document.body.appendChild(dummy);
		dummy.setAttribute('id', 'dummy_id');
		document.getElementById('dummy_id').value = link;
		dummy.select();
		document.execCommand('copy');
		document.body.removeChild(dummy);
	};

	const openScheduler = () => {
		const el = document.getElementById('scheduler-holder');
		el.classList.add('scheduler-present');
		setTimeout(() => {
			el.classList.add('scheduler-visible');
		});
	};

	const closeScheduler = () => {
		const el = document.getElementById('scheduler-holder');
		el.classList.remove('scheduler-visible');
		setTimeout(() => {
			el.classList.remove('scheduler-present');
		}, 300);
	};

	const openCreateSchedule = () => {
		setCreateScheduleOpen(true);
	};

	const closeCreateSchedule = () => {
		setCreateScheduleOpen(false);
		setCreateScheduleStarted(false);
	};

	const [newNotifications, setNewNotifications] = useState(false);

	const showNotifications = (el: any) => {
		el.classList.add('notifications-active');
		setTimeout(() => {
			el.classList.add('notifications-visible');
		});
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User opened their notifications',
				properties: {
					inHeader: true
				}
			});
		}
	};

	const hideNotifications = (el: any) => {
		el.classList.remove('notifications-visible');
		setNewNotifications(false);
		setTimeout(() => {
			el.classList.remove('notifications-active');
		}, 500);
	};

	const toggleNotifications = () => {
		const el = document.getElementsByClassName(
			'notifications-holder-outer'
		)[0];
		if (el.classList.contains('notifications-active')) {
			hideNotifications(el);
			postNotificationsRead();
		} else {
			showNotifications(el);
		}
	};

	const [newFeaturesKey, setNewFeaturesKey] = useState({});

	useEffect(() => {
		if (
			typeof userNotifications === 'object' &&
			Object.keys(userNotifications).length > 0
		) {
			if (
				userNotifications.replies > 0 ||
				userNotifications.opens > 0 ||
				userNotifications.features?.length > 0
			) {
				setNewNotifications(true);
				const keyObj = {};
				if (userNotifications?.features) {
					userNotifications.features.map(newNoti => {
						keyObj[newNoti.id] = true;
					});
				}
				setNewFeaturesKey(keyObj);
			}
		}
	}, [userNotifications]);

	const twoFactorAuthClickHandler = () => {
		setShow2faModal(true);

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User pressed Two-factor auth button in menu'
			});
		}
	};

	return (
		<>
			{fbFreemiumSuccess && successModal ? (
				<div className="done-screen-holder">
					<CheckmarkIcon className="success-checkmark" />
					<div className="done-screen">
						Success! Your email is verified
					</div>
					<div className="done-screen-paragraph">
						<span>
							You're now ready to connect your Facebook account
						</span>
					</div>
					<div className="done-screen-button-wrapper">
						<button
							type="submit"
							className="done-screen-button"
							onClick={() => {
								setSuccessModal(false);
							}}
						>
							Done
						</button>
					</div>
				</div>
			) : (
				<></>
			)}
			<div className="notifications-holder-outer">
				<div
					className="notifications-holder-bg"
					onClick={toggleNotifications}
				/>
				<div className="notifications-holder">
					<div className="notifications-header">Notifications</div>
					{userNotifications?.replies > 0 ||
					userNotifications?.opens > 0 ||
					!!userNotifications?.features?.length ||
					!!userNotifications?.featuresAll?.length ? (
						<>
							{userNotifications?.opens > 0 && (
								<Link
									className={`notification-item-holder ${!newNotifications ? 'read-notification' : ''}`}
									to="/communication-center?opens=yes"
									onClick={() => {
										toggleNotifications();

										if (user && user.email) {
											sendAnalytics({
												type: 'track',
												userId: user.email,
												eventName:
													'User clicked notification',
												properties: {
													type: 'New unchecked opened emails'
												}
											});
										}
									}}
								>
									{newNotifications ? (
										<NotificationsOpensIcon className="notification-icon" />
									) : (
										<NotificationsOpensIconWhite className="notification-icon" />
									)}
									You have {userNotifications.opens} new
									unchecked opened{' '}
									{userNotifications.opens > 1
										? 'emails'
										: 'email'}
									.
								</Link>
							)}
							{userNotifications?.replies > 0 && (
								<Link
									className={`notification-item-holder ${!newNotifications ? 'read-notification' : ''}`}
									to="/communication-center?replies=yes"
									onClick={() => {
										toggleNotifications();

										if (user && user.email) {
											sendAnalytics({
												type: 'track',
												userId: user.email,
												eventName:
													'User clicked notification',
												properties: {
													type: 'New unchecked replies'
												}
											});
										}
									}}
								>
									{newNotifications ? (
										<NotificationsEmailIcon className="notification-icon" />
									) : (
										<NotificationsEmailIconWhite className="notification-icon" />
									)}
									You have {userNotifications.replies} new
									unchecked{' '}
									{userNotifications.replies > 1
										? 'replies'
										: 'reply'}
									.
								</Link>
							)}
							{userNotifications?.features?.length > 0 && (
								<>
									{userNotifications.features.map(noti => {
										return (
											<div
												key={Math.random()}
												className={`notification-item-holder ${!newNotifications ? 'read-notification' : ''}`}
												onClick={() => {
													if (user && user.email) {
														sendAnalytics({
															type: 'track',
															userId: user.email,
															eventName:
																'User clicked notification',
															properties: {
																type: `${noti.feature} is now available`
															}
														});
													}
												}}
											>
												{newNotifications ? (
													<NewFeatureIcon className="notification-icon" />
												) : (
													<NewFeatureIconWhite className="notification-icon" />
												)}
												{noti.feature} is now available
											</div>
										);
									})}
								</>
							)}
							{userNotifications?.featuresAll?.length > 0 && (
								<>
									{userNotifications.featuresAll.map(noti => {
										if (!newFeaturesKey[noti.id]) {
											return (
												<div
													className={`notification-item-holder ${!newNotifications ? 'read-notification' : ''}`}
													key={Math.random()}
													onClick={() => {
														if (
															user &&
															user.email
														) {
															sendAnalytics({
																type: 'track',
																userId: user.email,
																eventName:
																	'User clicked notification',
																properties: {
																	type: `${noti.feature} is now available`
																}
															});
														}
													}}
												>
													<NewFeatureIconWhite className="notification-icon" />
													{noti.feature} is now
													available
												</div>
											);
										}
									})}
								</>
							)}
						</>
					) : (
						<div className="notifications-empty">
							You currently have no new notifications
						</div>
					)}
				</div>
			</div>
			{createScheduleStarted ? (
				<>
					<div className="create-schedule-link-holder-outer">
						<div
							onClick={closeCreateSchedule}
							className="create-schedule-link-clickable-bg"
						/>
						{createScheduleOpen ? (
							<div className="create-schedule-link-holder">
								<XIcon
									onClick={closeCreateSchedule}
									className="create-schedule-link-close-button"
								/>
								<div className="create-schedule-link-text-holder">
									<div className="create-schedule-link-headline">
										Get started scheduling meetings through
										Resquared
									</div>
									<div className="create-schedule-link-subheadline">
										Connect your Calendar to start
										scheduling meetings.
										<br />
										Turn on your permissions by logging into
										your email with your email credentials.
									</div>
								</div>
								<a href="/email-calendar-onboarding">
									<div className="create-schedule-link-button-holder">
										<GoogleButtonIcon className="email-logo email-logo-a" />
										<MsButtonIcon className="email-logo email-logo-b" />
										<EmailSecondary className="email-logo email-logo-c" />
										Connect My Calendar
									</div>
								</a>
							</div>
						) : (
							<div className="scheduler-loading-ball-holder">
								<div className="scheduler-loading-ball scheduler-loading-ball-1" />
								<div className="scheduler-loading-ball scheduler-loading-ball-2" />
								<div className="scheduler-loading-ball scheduler-loading-ball-3" />
								<div className="scheduler-loading-ball scheduler-loading-ball-4" />
							</div>
						)}
					</div>
				</>
			) : (
				''
			)}
			{!user?.facebookFreemium ? (
				<>
					<div
						className="scheduler-modal-holder"
						id="scheduler-holder"
					>
						<div
							className="scheduler-modal-bg"
							onClick={closeScheduler}
						/>
						<div className="scheduler-modal-inner">
							<XIcon
								className="scheduler-modal-close-button"
								onClick={closeScheduler}
							/>
							<div className="scheduler-modal-headline">
								Schedule a Meeting
							</div>
							<div className="scheduler-modal-subheadline">
								Copy the link to your desired meeting length and
								share to allow someone to schedule a meeting
								directly on your calendar
							</div>
							<div className="scheduler-modal-links-holder">
								<div
									className="scheduler-modal-link-box"
									onClick={e => {
										copyScheduleLink(e, 15);
									}}
								>
									<div className="scheduler-modal-link-time">
										15
									</div>
									<div className="scheduler-modal-link-time-label">
										Minutes
									</div>
									<div className="scheduler-modal-link-border" />
									<div className="scheduler-modal-link-time-copy">
										Copy
									</div>
									<div className="scheduler-modal-link-time-copied">
										<CheckMarkGreen className="scheduler-modal-link-checkmark" />
										Copied
									</div>
								</div>
								<div
									className="scheduler-modal-link-box"
									onClick={e => {
										copyScheduleLink(e, 30);
									}}
								>
									<div className="scheduler-modal-link-time">
										30
									</div>
									<div className="scheduler-modal-link-time-label">
										Minutes
									</div>
									<div className="scheduler-modal-link-border" />
									<div className="scheduler-modal-link-time-copy">
										Copy
									</div>
									<div className="scheduler-modal-link-time-copied">
										<CheckMarkGreen className="scheduler-modal-link-checkmark" />
										Copied
									</div>
								</div>
								<div
									className="scheduler-modal-link-box"
									onClick={e => {
										copyScheduleLink(e, 45);
									}}
								>
									<div className="scheduler-modal-link-time">
										45
									</div>
									<div className="scheduler-modal-link-time-label">
										Minutes
									</div>
									<div className="scheduler-modal-link-border" />
									<div className="scheduler-modal-link-time-copy">
										Copy
									</div>
									<div className="scheduler-modal-link-time-copied">
										<CheckMarkGreen className="scheduler-modal-link-checkmark" />
										Copied
									</div>
								</div>
							</div>
						</div>
					</div>
				</>
			) : (
				<></>
			)}
			{!user?.facebookFreemium ? (
				<>
					<div className="flex w-1/2 flex-shrink flex-row items-center md:text-xs lg:text-base">
						<Logo />
						<NavBar
							selectedLocationFilter={selectedLocationFilter}
							setSelectedLocationFilter={
								setSelectedLocationFilter
							}
							selectedListFilter={selectedListFilter}
							setSelectedListFilter={setSelectedListFilter}
							setAddLocationOpen={setAddLocationOpen}
							closeLocationDropdown={closeLocationDropdown}
							locationChange={locationChange}
							listChange={listChange}
						/>
					</div>
				</>
			) : (
				<>
					<div className="flex w-1/2 flex-shrink flex-row items-center md:text-xs lg:text-base">
						<Link className="logo" redirect to="/adcampaign">
							<LogoShort width={83} height={32} />
						</Link>
						<div className="freemium-header font-semibold">
							{' '}
							&nbsp;- Facebook Ads
						</div>
					</div>
				</>
			)}
			<div className="header-nav-wrapper flex w-1/2 flex-grow flex-row items-center justify-end">
				<div className="header-icon-buttons-holder">
					{!user?.facebookFreemium ? (
						<>
							{user ? (
								<Link
									className={`header-icon-button ${pathname === '/scheduled-emails' ? 'link-page-active' : ''}`}
									rel="noopener noreferrer"
									to="/scheduled-emails"
									onClick={() => {
										sendAnalytics({
											type: 'track',
											userId: user.email,
											eventName:
												'User opened scheduled emails page'
										});
										setSelectedListFilter('');
										setSelectedLocationFilter('');
										listChange('');
										locationChange('');
									}}
								>
									<img
										src={ScheduledEmail}
										alt="Activity Reports"
										className="header-icon-button-normal"
									/>
									<img
										src={ScheduledEmailHovering}
										alt="Activity Reports"
										className="header-icon-button-hovering"
									/>
									<div className="header-icon-label">
										Scheduled Emails
									</div>
								</Link>
							) : (
								''
							)}
							<Link
								className={`header-icon-button ${pathname === '/communication-center' ? 'link-page-active' : ''}`}
								rel="noopener noreferrer"
								to="/communication-center"
								onClick={() => {
									if (user && user.email) {
										sendAnalytics({
											type: 'track',
											userId: user.email,
											eventName:
												'User opened communication center'
										});
										setSelectedListFilter('');
										setSelectedLocationFilter('');
										listChange('');
										locationChange('');
									}
								}}
							>
								<CommunicationCenterIcon className="header-icon-button-normal" />
								<CommunicationCenterIconHover className="header-icon-button-hovering" />
								<div className="header-icon-label">
									Communication Center
								</div>
								{newMessages && (
									<div className="has-unread-responses">
										<div className="has-unread-responses-inner" />
									</div>
								)}
							</Link>
							<Link
								className={`header-icon-button ${pathname === '/reports' ? 'link-page-active' : ''}`}
								rel="noopener noreferrer"
								to="/reports"
								onClick={() => {
									if (user && user.email) {
										sendAnalytics({
											type: 'track',
											userId: user.email,
											eventName:
												'User opened activity reports'
										});
									}

									setSelectedListFilter('');
									setSelectedLocationFilter('');
									listChange('');
									locationChange('');
								}}
							>
								<img
									src={ReportsIcon}
									alt="Activity Reports"
									className="header-icon-button-normal"
								/>
								<img
									src={ReportsIconHovering}
									alt="Activity Reports"
									className="header-icon-button-hovering"
								/>
								<div className="header-icon-label">
									Activity Reports
								</div>
							</Link>
							<Link
								className="header-icon-button knowledge-base"
								rel="noopener noreferrer"
								to="https://learn.re2.ai/"
								target="_blank"
								onClick={() => {
									if (user && user.email) {
										sendAnalytics({
											type: 'track',
											userId: user.email,
											eventName:
												'User opened knowledge base'
										});
									}
								}}
							>
								<img
									src={KnowledgeBaseIcon}
									alt="Knowledge Base"
									className="header-icon-button-normal knowledge-base-icon"
								/>
								<img
									src={KnowledgeBaseIconHovering}
									alt="Knowledge Base"
									className="header-icon-button-hovering knowledge-base-icon-hovering"
								/>
								<div className="header-icon-label knowledge-base">
									Knowledge Base
								</div>
							</Link>
							{user && (
								<div
									className="header-icon-button"
									onClick={() => {
										toggleNotifications();
									}}
								>
									<NotificationBell className="header-icon-button-normal" />
									<NotificationBellHover className="header-icon-button-hovering notification-bell-hover" />
									<div className="header-icon-label">
										Notifications
									</div>
									{newNotifications && (
										<div className="notification-bell-active">
											<div className="notification-bell-active-inner" />
										</div>
									)}
								</div>
							)}
							{user && (
								<div
									className="header-icon-button scheduler"
									key={Math.random()}
									onClick={() => {
										if (
											user.nylasSyncStatus !==
												'running' &&
											user.nylasSyncStatus !==
												'nothing' &&
											user.nylasSyncStatus !==
												'partial' &&
											user.nylasSyncStatus !== undefined
										) {
											setNylasSyncStatus(false);
											return;
										}
										openCreateMeeting();
										if (user && user.email) {
											sendAnalytics({
												type: 'track',
												userId: user.email,
												eventName:
													'User pressed Schedule Meeting in menu',
												properties: {
													inHeader: true
												}
											});
										}
									}}
								>
									<ScheduleIcon className="header-icon-button-normal" />
									<ScheduleIconHover className="header-icon-button-hovering scheduler-hovering" />
									<div className="header-icon-label">
										Scheduler
									</div>
								</div>
							)}
						</>
					) : (
						<>
							<Link
								className="header-icon-button knowledge-base fb-freemium"
								rel="noopener noreferrer"
								to="https://learn.re2.ai/"
								target="_blank"
								onClick={() => {
									if (user && user.email) {
										sendAnalytics({
											type: 'track',
											userId: user.email,
											eventName:
												'User opened knowledge base'
										});
									}
								}}
							>
								<img
									src={KnowledgeBaseIcon}
									alt="Knowledge Base"
									className="header-icon-button-normal knowledge-base-icon"
								/>
								<img
									src={KnowledgeBaseIconHovering}
									alt="Knowledge Base"
									className="header-icon-button-hovering knowledge-base-icon-hovering"
								/>
								<div className="header-icon-label knowledge-base">
									Knowledge Base
								</div>
							</Link>
						</>
					)}
				</div>

				<div className="ml-6">
					<Popup
						trigger={
							<button
								type="button"
								id="profile-picture-button"
								className="font-semibold text-orange no-underline hover:no-underline"
							>
								<span className="circle-icon">
									<span>
										{user.email
											? user.email[0].toUpperCase()
											: ' '}
									</span>
								</span>
							</button>
						}
						onSelectionClose
						event={PopupTrigger.Hover}
						analytic="User opened the profile menu"
						user={user}
						containerPaddingY={2}
						contentPaddingX={0}
						align={PopupAlign.Right}
					>
						<div className="header--tips__content">
							<div
								type="button"
								className="menu-email-button block whitespace-nowrap px-8 py-2"
							>
								{user.email}
							</div>
							{/* <button
                type="button"
                onClick={() => {
                  handleChatOpen()
                  if (user && user.email) {
                    sendAnalytics({
                      type: 'track',
                      userId: user.email,
                      eventName: 'User pressed Chat Assistant button in menu'
                    })
                  }
                }}
                className="block whitespace-nowrap py-2 px-8 hover:text-orange transition-colors ease-in-out duration-300"
              >
                Chat Assistant
              </button> */}
							<button
								type="button"
								onClick={() => {
									onPasswordReset();
									if (user && user.email) {
										sendAnalytics({
											type: 'track',
											userId: user.email,
											eventName:
												'User pressed Reset Password button in menu'
										});
									}
								}}
								className="block whitespace-nowrap px-8 py-2 transition-colors duration-300 ease-in-out hover:text-orange"
							>
								Reset Password
							</button>
							{user?.features?.otp && (
								<button
									type="button"
									onClick={twoFactorAuthClickHandler}
									className="block whitespace-nowrap px-8 py-2 transition-colors duration-300 ease-in-out hover:text-orange"
								>
									Two-factor Auth
								</button>
							)}
							{!user?.facebookFreemium ? (
								<>
									<button
										type="button"
										onClick={() => {
											toggleAddUserModal(true);
											if (user && user.email) {
												sendAnalytics({
													type: 'track',
													userId: user.email,
													eventName:
														'User pressed Add New User button in menu'
												});
											}
										}}
										className="block whitespace-nowrap px-8 py-2 transition-colors duration-300 ease-in-out hover:text-orange"
									>
										Add a New User
									</button>
								</>
							) : (
								<></>
							)}
							{!user?.facebookFreemium && (
								<button
									type="button"
									onClick={() => {
										toggleAddBccModal(true);
										if (user && user.email) {
											sendAnalytics({
												type: 'track',
												userId: user.email,
												eventName:
													'User pressed Add New BCC button in menu'
											});
										}
									}}
									className="block whitespace-nowrap px-8 py-2 transition-colors duration-300 ease-in-out hover:text-orange"
								>
									{user?.crmBcc &&
									Object.keys(user?.crmBcc).length > 0 ? (
										<>Edit CRM Bcc settings</>
									) : (
										<>Add CRM Bcc</>
									)}
								</button>
							)}
							{!user?.facebookFreemium &&
							!user?.re2SharedDomain ? (
								<>
									<Route
										render={({ history }) => (
											<button
												type="button"
												className="block whitespace-nowrap px-8 py-2 transition-colors duration-300 ease-in-out hover:text-orange"
												onClick={() => {
													if (user && user.email) {
														sendAnalytics({
															type: 'track',
															userId: user.email,
															eventName:
																'User pressed Verify Email link in menu'
														});
													}
													setSelectedListFilter('');
													setSelectedLocationFilter(
														''
													);
													listChange('');
													locationChange('');
													history.push(
														'/verify-email-onboarding'
													);
												}}
											>
												Verify Your Email
											</button>
										)}
									/>
								</>
							) : (
								<></>
							)}
							<Route
								render={({ history }) => (
									<button
										type="button"
										className="block whitespace-nowrap px-8 py-2 transition-colors duration-300 ease-in-out hover:text-orange"
										onClick={() => {
											if (user && user.email) {
												sendAnalytics({
													type: 'track',
													userId: user.email,
													eventName:
														'User pressed Demand Reports link in menu'
												});
											}
											setSelectedListFilter('');
											setSelectedLocationFilter('');
											listChange('');
											locationChange('');
											history.push('/demand-reports');
										}}
									>
										Demand Reports
									</button>
								)}
							/>
							<button
								className="block whitespace-nowrap px-8 py-2 transition-colors duration-300 ease-in-out hover:text-orange"
								id="user-dropdown-logout"
								type="button"
								onClick={() => {
									if (user && user.email) {
										sendAnalytics({
											type: 'track',
											userId: user.email,
											eventName:
												'User pressed Log Out button in menu'
										});
									}
									window?.productFruits?.services?.destroy();
									logoutUser();
								}}
							>
								Log Out
							</button>
						</div>
					</Popup>
				</div>
			</div>
			<div className="popup-hamburger">
				<input
					type="checkbox"
					className="popup__checkbox"
					id="popup-toggle"
				/>

				<label htmlFor="popup-toggle" className="popup__button">
					<span className="popup__icon">&nbsp;</span>
				</label>
				<div className="popup__background" />
				<nav className="popup__navigation">
					<ul>
						{user ? (
							<Route
								render={({ history }) => (
									<li>
										<label
											htmlFor="popup-toggle"
											className="popup__navigation-link"
											onClick={() => {
												if (user && user.email) {
													sendAnalytics({
														type: 'track',
														userId: user.email,
														eventName:
															'User opened scheduled emails page'
													});
												}
												history.push(
													'/scheduled-emails'
												);
											}}
										>
											Scheduled Emails
										</label>
									</li>
								)}
							/>
						) : (
							''
						)}
						{!user?.facebookFreemium ? (
							<>
								<Route
									render={({ history }) => (
										<li>
											<label
												htmlFor="popup-toggle"
												className="popup__navigation-link"
												onClick={() => {
													if (user && user.email) {
														sendAnalytics({
															type: 'track',
															userId: user.email,
															eventName:
																'User opened communication center'
														});
													}
													history.push(
														'/communication-center'
													);
												}}
											>
												Communication Center
											</label>
										</li>
									)}
								/>
								<Route
									render={({ history }) => (
										<li>
											<label
												htmlFor="popup-toggle"
												className="popup__navigation-link"
												onClick={() => {
													if (user && user.email) {
														sendAnalytics({
															type: 'track',
															userId: user.email,
															eventName:
																'User opened activity reports'
														});
													}
													history.push('/reports');
												}}
											>
												Activity Reports
											</label>
										</li>
									)}
								/>
							</>
						) : (
							<></>
						)}

						{/* <li onClick={handleChatOpen}>
              <label htmlFor="popup-toggle" className="popup__navigation-link">
                Chat Assistant
              </label>
            </li> */}
						<li>
							<label
								htmlFor="popup-toggle"
								className="popup__navigation-link"
								onClick={() => {
									onPasswordReset();
									if (user && user.email) {
										sendAnalytics({
											type: 'track',
											userId: user.email,
											eventName:
												'User pressed Reset Password button in menu'
										});
									}
								}}
							>
								Reset Password
							</label>
						</li>
						{user?.features?.otp && (
							<li>
								<label
									htmlFor="popup-toggle"
									className="popup__navigation-link"
									onClick={twoFactorAuthClickHandler}
								>
									Two-factor Auth
								</label>
							</li>
						)}
						{!user?.facebookFreemium ? (
							<>
								<li>
									<label
										htmlFor="popup-toggle"
										className="popup__navigation-link"
										onClick={() => {
											toggleAddUserModal(true);
											if (user && user.email) {
												sendAnalytics({
													type: 'track',
													userId: user.email,
													eventName:
														'User pressed Add New User button in menu'
												});
											}
										}}
									>
										Add a New User
									</label>
								</li>
							</>
						) : (
							<></>
						)}
						{!user?.facebookFreemium ? (
							<>
								<li>
									<label
										htmlFor="popup-toggle"
										className="popup__navigation-link"
										onClick={() => {
											toggleAddBccModal(true);
											if (user && user.email) {
												sendAnalytics({
													type: 'track',
													userId: user.email,
													eventName:
														'User pressed Add New BCC button in menu'
												});
											}
										}}
									>
										{user?.crmBcc &&
										Object.keys(user?.crmBcc).length > 0 ? (
											<>Edit CRM Bcc settings</>
										) : (
											<>Add CRM Bcc</>
										)}
									</label>
								</li>
								{user?.features?.verifyEmail != false &&
									!user?.re2SharedDomain && (
										<Route
											render={({ history }) => (
												<li>
													<label
														htmlFor="popup-toggle"
														className="popup__navigation-link"
														onClick={() => {
															if (
																user &&
																user.email
															) {
																sendAnalytics({
																	type: 'track',
																	userId: user.email,
																	eventName:
																		'User pressed Verify Email link in menu'
																});
															}
															history.push(
																'/email-onboarding'
															);
														}}
													>
														Verify Your Email
													</label>
												</li>
											)}
										/>
									)}
								<Route
									render={({ history }) => (
										<li>
											<label
												htmlFor="popup-toggle"
												className="popup__navigation-link"
												onClick={() => {
													if (user && user.email) {
														sendAnalytics({
															type: 'track',
															userId: user.email,
															eventName:
																'User pressed Demand Reports link in menu'
														});
													}
													history.push(
														'/demand-reports'
													);
												}}
											>
												Demand Reports
											</label>
										</li>
									)}
								/>
							</>
						) : (
							<></>
						)}
					</ul>
					<span />
					<span
						id="popup__navigation_logout"
						className="popup__navigation-link logout"
						onClick={() => {
							window?.productFruits?.services?.destroy();
							logoutUser();
						}}
					>
						Log out
					</span>
				</nav>
			</div>
		</>
	);
};

export default Header;
